/**
 * Pure date utility functions for timesheet week navigation.
 *
 * These are intentionally free of React dependencies so they can be
 * tested in isolation with standard Jest.
 */

/**
 * Format a Date object as YYYY-MM-DD using local timezone.
 * Avoids UTC-conversion issues that arise from `toISOString()`.
 */
export const toLocalDateString = (date) => {
  const year  = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day   = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Advance (or rewind) a YYYY-MM-DD date string by the given number of days.
 */
export const addDaysToDateString = (dateStr, days) => {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  date.setDate(date.getDate() + days);
  return toLocalDateString(date);
};

/**
 * Return an array of 7 date objects representing the week that contains
 * the given date, anchored to the configured start day.
 *
 * @param {string} startDateStr  - any YYYY-MM-DD date within the desired week
 * @param {number} weekStartDay  - 0 = Sunday (CA default), 1 = Monday (US)
 * @returns {{ dateStr, dayOfWeek, dayNum, month }[]}
 */
export const getWeekDateStrings = (startDateStr, weekStartDay = 0) => {
  const [year, month, day] = startDateStr.split('-').map(Number);
  const start = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = start.getDay();
  const daysBack  = (dayOfWeek - weekStartDay + 7) % 7;
  start.setDate(start.getDate() - daysBack);

  const dates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push({
      dateStr:   toLocalDateString(date),
      dayOfWeek: date.getDay(),
      dayNum:    date.getDate(),
      month:     date.getMonth()
    });
  }
  return dates;
};
